import React, { useState, useEffect } from "react";

import axios from "../../utils/axios";

import { useLocation, useNavigate } from "react-router-dom";

import Icon from "../AppIcon";

import Button from "./Button";

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const [generalSettings, setGeneralSettings] = useState({
    organization_name: "",
    company_logo_url: null,
    favicon_url: null,
  });

  const [userPermissions, setUserPermissions] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [expandedSections, setExpandedSections] = useState({
    operations: true,
    contracts: true,
    vendors: true,
    procurement: true,
    servicedesk: true,
    departments: true,
    compliance: true,
    analytics: true,
  });

  // ==========================================
  // FETCH GENERAL SETTINGS
  // ==========================================

  const fetchGeneralSettings = async () => {
    try {
      const response = await axios.get("/general-settings");

      if (response.data?.success && response.data?.data) {
        setGeneralSettings(response.data.data);

        // Dynamic favicon
        if (response.data.data.favicon_url) {
          let favicon = document.querySelector("link[rel='icon']");

          if (!favicon) {
            favicon = document.createElement("link");
            favicon.rel = "icon";
            document.head.appendChild(favicon);
          }

          favicon.href = response.data.data.favicon_url;
        }
      }
    } catch (error) {
      console.error("Failed to fetch general settings:", error);
    }
  };

  // ==========================================
  // FETCH USER PERMISSIONS
  // ==========================================

  const fetchUserPermissions = async () => {
    try {
      const response = await axios.get("/me");
      const user = response.data?.user;
      setCurrentUser(user);

      const permissions = user?.role?.permissions_grouped || {};
      const roleName = (user?.role?.name || "").toLowerCase().trim();

      if (roleName === "it admin" || roleName === "super admin" || roleName === "admin") {
        permissions.all = true;
      }

      setUserPermissions(permissions);
    } catch (error) {
      console.error("Failed to fetch user permissions:", error);
      setUserPermissions({});
    }
  };

  // ==========================================
  // LOAD DATA
  // ==========================================

  useEffect(() => {
    fetchGeneralSettings();
    fetchUserPermissions();
  }, []);

  // ==========================================
  // CHECK PERMISSION
  // ==========================================

  const hasPermission = (module, permission = "read") => {
    // Super Admin / IT Admin bypass
    if (userPermissions?.all === true) {
      return true;
    }

    const roleName = (currentUser?.role?.name || "").toLowerCase().trim();
    if (roleName === "it admin" || roleName === "super admin" || roleName === "admin") {
      return true;
    }

    // Normal role permissions
    return (
      Array.isArray(userPermissions?.[module]) &&
      userPermissions[module].includes(permission)
    );
  };

  // ==========================================
  // NAVIGATION
  // ==========================================

  const navigationSections = [
    // ==========================================
    // ASSET OPERATIONS
    // ==========================================

    {
      id: "operations",
      title: "Asset Operations",
      items: [
        {
          label: "Asset Dashboard",
          path: "/asset-management-dashboard",
          icon: "LayoutDashboard",
          tooltip: "Inventory grid, lifecycle pipeline, software licences, asset recovery & floor plan",
          permission: {
            module: "assets",
            action: "read",
          },
        },
        // {
        //   label: "Asset Lifecycle",
        //   path: "/asset-management-dashboard?tab=lifecycle",
        //   icon: "Workflow",
        //   tooltip: "5-stage lifecycle pipeline, assignment, transfer, audit and decommissioning",
        //   permission: {
        //     module: "assets",
        //     action: "read",
        //   },
        // },
        // {
        //   label: "Recovery & Clearance",
        //   path: "/asset-management-dashboard?tab=recovery",
        //   icon: "ShieldCheck",
        //   tooltip: "Employee exit clearance, hardware surrender, credential revocation & certificates",
        //   permission: {
        //     module: "assets",
        //     action: "read",
        //   },
        // },
        {
          label: "Asset Details",
          path: "/asset-detail-management",
          icon: "Package",
          tooltip: "Detailed asset record management",
          permission: {
            module: "assets",
            action: "read",
          },
        },
      ],
    },

    // ==========================================
    // CONTRACT MANAGEMENT
    // ==========================================

    // {
    //   id: "contracts",
    //   title: "Contract Management",
    //   items: [
    //     {
    //       label: "Contracts",
    //       path: "/contract-management",
    //       icon: "FileText",
    //       tooltip: "Manage contracts, AMC, warranties and renewals",
    //     },
    //   ],
    // },

    // ==========================================
    // VENDOR MANAGEMENT
    // ==========================================

    {
      id: "vendors",
      title: "Vendor Management",
      items: [
        {
          label: "Vendors",
          path: "/vendor-management",
          icon: "Building2",
          tooltip: "Manage suppliers, contacts and vendor information",
          permission: {
            module: "vendors",
            action: "read",
          },
        },
        {
          label: "Vendor Categories",
          path: "/vendor-categories",
          icon: "Tags",
          tooltip: "Create and manage vendor categories",
          permission: {
            module: "vendors",
            action: "read",
          },
        },
        {
          label: "Vendor Types",
          path: "/vendor-types",
          icon: "ListTree",
          tooltip: "Manage vendor types",
          permission: {
            module: "vendors",
            action: "read",
          },
        },
      ],
    },

    // ==========================================
    // PROCUREMENT MANAGEMENT
    // ==========================================

    {
      id: "procurement",
      title: "Procurement Management",
      items: [
        {
          label: "Procurement",
          path: "/procurement-management",
          icon: "ShoppingCart",
          tooltip:
            "Manage procurement requests, purchase orders and deliveries",
          permission: {
            module: "procurement",
            action: "read",
          },
        },
        {
          label: "Procurement Categories",
          path: "/procurement-categories",
          icon: "Tags",
          tooltip: "Create and manage procurement categories",
          permission: {
            module: "procurement",
            action: "read",
          },
        },
      ],
    },

    // ==========================================
    // SERVICE REQUESTS
    // ==========================================

    {
      id: "servicedesk",
      title: "Service Requests",
      items: [
        {
          label: "Service Requests",
          path: "/service-desk",
          icon: "PackageCheck",
          tooltip:
            "Employee requests for hardware, software licences, loaners and accessories",
          permission: {
            module: "servicedesk",
            action: "read",
          },
        },
      ],
    },
    {
      id: "departments",
      title: "Organization",
      items: [
        {
          label: "Department Management",
          path: "/department-management",
          icon: "Building2",
          tooltip: "Manage departments, managers, employees and budgets",
          permission: {
            module: "departments",
            action: "read",
          },
        },
      ],
    },

    {
      id: "contracts",
      title: "Contract Management",
      items: [
        {
          label: "Contracts",
          path: "/contract-management",
          icon: "FileText",
          tooltip: "Manage contracts, AMC, warranties and renewals",
          permission: {
            module: "contracts",
            action: "read",
          },
        },
        {
          label: "Contract Categories",
          path: "/contract-categories",
          icon: "Folder",
          tooltip: "Create and manage contract categories",
          permission: {
            module: "contracts",
            action: "read",
          },
        },
        {
          label: "Contract Types",
          path: "/contract-types",
          icon: "ListTree",
          tooltip: "Manage contract types",
          permission: {
            module: "contracts",
            action: "read",
          },
        },
      ],
    },

    // ==========================================
    // COMPLIANCE & MAINTENANCE
    // ==========================================

    {
      id: "compliance",
      title: "Compliance & Maintenance",
      items: [
        {
          label: "Audit Workflow",
          path: "/quarterly-audit-workflow",
          icon: "ClipboardCheck",
          tooltip: "Quarterly audit processes",
          permission: {
            module: "audit",
            action: "read",
          },
        },

        {
          label: "Warranty Center",
          path: "/warranty-management-center",
          icon: "Shield",
          tooltip:
            "Warranty management and tracking",
          permission: {
            module: "warranty",
            action: "read",
          },
        },
      ],
    },

    // ==========================================
    // ANALYTICS & ADMINISTRATION
    // ==========================================

    {
      id: "analytics",
      title: "Analytics & Administration",
      items: [
        {
          label: "Analytics Dashboard",
          path: "/asset-analytics-dashboard",
          icon: "BarChart3",
          tooltip:
            "Asset analytics and insights",
          permission: {
            module: "analytics",
            action: "read",
          },
        },

        {
          label: "User Management",
          path: "/user-management-console",
          icon: "Users",
          tooltip:
            "User management console",
          permission: {
            module: "users",
            action: "read",
          },
        },

        {
          label: "System Config",
          path: "/system-configuration-panel",
          icon: "Settings",
          tooltip:
            "System configuration panel",
          permission: {
            module: "settings",
            action: "read",
          },
        },

        {
          label: "Help & Support",
          path: "/help-support",
          icon: "HelpCircle",
          tooltip:
            "Knowledge base, documentation, FAQs, and support tickets",
        },
      ],
    },
  ];

  // ==========================================
  // NAVIGATION FUNCTIONS
  // ==========================================

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleSection = (sectionId) => {
    if (!isCollapsed) {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    }
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  // ==========================================
  // FILTER SIDEBAR BY PERMISSION
  // ==========================================

  const visibleNavigationSections = navigationSections
    .map((section) => ({
      ...section,

      items: section.items.filter((item) => {
        // Item without permission = always visible
        if (!item.permission) {
          return true;
        }

        return hasPermission(
          item.permission.module,
          item.permission.action
        );
      }),
    }))
    .filter((section) => section.items.length > 0);

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <aside
      className={`fixed left-0 top-0 bottom-0 z-50 overflow-hidden transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-64 lg:w-72"
      }`}
      style={{
        backgroundColor: "#141720",
      }}
    >
      <div className="flex flex-col h-screen min-h-0">

        {/* ==========================================
            LOGO SECTION
        ========================================== */}

        <div
          className="flex-shrink-0 flex items-center justify-between p-4 border-b cursor-pointer hover:opacity-80 transition-opacity"
          style={{
            borderBottomColor: "rgba(255, 255, 255, 0.08)",
          }}
          onClick={handleLogoClick}
        >
          {!isCollapsed ? (
            <div className="flex items-center space-x-3 overflow-hidden">
              <div
                className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center"
                style={{
                  backgroundColor: "#ffffff",
                }}
              >
                {generalSettings?.company_logo_url ? (
                  <img
                    src={generalSettings.company_logo_url}
                    alt="Logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Icon
                    name="Package"
                    size={20}
                    color="#141720"
                  />
                )}
              </div>

              <div className="flex flex-col min-w-0">
                <span
                  className="text-base lg:text-lg font-semibold truncate"
                  style={{
                    color: "rgba(255, 255, 255, 0.95)",
                  }}
                >
                  {generalSettings?.organization_name || "Kerol"}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex justify-center w-full">
              <div
                className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden flex items-center justify-center"
                style={{
                  backgroundColor: "#ffffff",
                }}
              >
                {generalSettings?.company_logo_url ? (
                  <img
                    src={generalSettings.company_logo_url}
                    alt="Logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Icon
                    name="Package"
                    size={20}
                    color="#141720"
                  />
                )}
              </div>
            </div>
          )}

          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              className="h-8 w-8 flex-shrink-0"
              style={{
                color: "rgba(255, 255, 255, 0.6)",
              }}
            >
              <Icon
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"}
                size={16}
              />
            </Button>
          )}
        </div>

        {/* ==========================================
            SCROLLABLE NAVIGATION
        ========================================== */}

        <div
          className="flex-1 min-h-0 py-4 overflow-y-auto overflow-x-hidden"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor:
              "rgba(255, 255, 255, 0.25) transparent",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div className="space-y-4 lg:space-y-6 pb-4">

            {visibleNavigationSections?.map((section) => (
              <div
                key={section?.id}
                className="px-2 lg:px-3"
              >

                {/* ==========================================
                    SECTION HEADER
                ========================================== */}

                {!isCollapsed && (
                  <button
                    type="button"
                    onClick={() =>
                      toggleSection(section?.id)
                    }
                    className="flex items-center justify-between w-full px-2 lg:px-3 py-1.5 lg:py-2 text-xs font-medium uppercase tracking-wide transition-smooth hover:opacity-80"
                    style={{
                      color: "rgba(255, 255, 255, 0.5)",
                    }}
                  >
                    <span className="truncate">
                      {section?.title}
                    </span>

                    <Icon
                      name={
                        expandedSections?.[section?.id]
                          ? "ChevronDown"
                          : "ChevronRight"
                      }
                      size={14}
                      className="flex-shrink-0"
                    />
                  </button>
                )}

                {/* ==========================================
                    SECTION ITEMS
                ========================================== */}

                <div
                  className={`space-y-0.5 lg:space-y-1 ${
                    isCollapsed
                      ? "mt-0"
                      : expandedSections?.[section?.id]
                      ? "mt-1 lg:mt-2"
                      : "hidden"
                  }`}
                >
                  {section?.items?.map((item) => {
                    const isActive = isActiveRoute(
                      item?.path
                    );

                    return (
                      <div
                        key={item?.path}
                        className="relative group"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleNavigation(item?.path)
                          }
                          className={`flex items-center w-full rounded-md transition-all duration-200 ${
                            isCollapsed
                              ? "justify-center px-0 py-2 lg:py-2.5"
                              : "justify-start space-x-2 lg:space-x-3 px-2 lg:px-3 py-1.5 lg:py-2.5"
                          }`}
                          title={
                            isCollapsed
                              ? item?.tooltip
                              : ""
                          }
                          style={{
                            backgroundColor: isActive
                              ? "#ffffff"
                              : "transparent",

                            color: isActive
                              ? "#141720"
                              : "rgba(255, 255, 255, 0.7)",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor =
                                "rgba(255, 255, 255, 0.08)";

                              e.currentTarget.style.color =
                                "rgba(255, 255, 255, 0.9)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";

                              e.currentTarget.style.color =
                                "rgba(255, 255, 255, 0.7)";
                            }
                          }}
                        >
                          <Icon
                            name={item?.icon}
                            size={16}
                            className="flex-shrink-0"
                            style={{
                              color: isActive
                                ? "#141720"
                                : "rgba(255, 255, 255, 0.5)",
                            }}
                          />

                          {!isCollapsed && (
                            <span className="font-medium text-sm truncate">
                              {item?.label}
                            </span>
                          )}
                        </button>

                        {/* ==========================================
                            COLLAPSED TOOLTIP
                        ========================================== */}

                        {isCollapsed && (
                          <div
                            className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50"
                            style={{
                              backgroundColor: "#1e2438",
                              color: "rgba(255, 255, 255, 0.9)",
                            }}
                          >
                            {item?.label}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ==========================================
                    SECTION SEPARATOR
                ========================================== */}

                {!isCollapsed &&
                  section?.id !== "analytics" && (
                    <div
                      className="mt-3 lg:mt-4"
                      style={{
                        borderBottom:
                          "1px solid rgba(255, 255, 255, 0.08)",
                      }}
                    />
                  )}
              </div>
            ))}

          </div>
        </div>

        {/* ==========================================
            SIDEBAR FOOTER
        ========================================== */}

        <div
          className="flex-shrink-0 p-3 lg:p-4"
          style={{
            borderTop:
              "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          {!isCollapsed ? (
            <div
              className="flex items-center space-x-2 lg:space-x-3 text-xs"
              style={{
                color: "rgba(255, 255, 255, 0.4)",
              }}
            >
              <Icon name="Clock" size={12} />

              <span className="truncate">
                Last updated: Aug 19, 2025
              </span>
            </div>
          ) : (
            <div className="flex justify-center">
              <Icon
                name="Clock"
                size={14}
                style={{
                  color: "rgba(255, 255, 255, 0.4)",
                }}
              />
            </div>
          )}
        </div>

      </div>
    </aside>
  );
};

export default Sidebar;